import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Link, useNavigate } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import { registerUser } from '../../api/authApi';
import { useAuth } from '../../context/AuthContext';

const schema = yup.object({
  name: yup
    .string()
    .required('Full name is required')
    .min(2, 'Name must be at least 2 characters'),
  organization_name: yup
    .string()
    .required('Organization name is required')
    .min(2, 'Organization name must be at least 2 characters'),
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
  password_confirmation: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords do not match'),
});

export default function RegisterPage() {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [apiError, setApiError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onTouched',
  });

  const onSubmit = async (data) => {
    setApiError('');
    try {
      const response = await registerUser({
        full_name: data.name,
        email: data.email,
        password: data.password,
        password_confirmation: data.password_confirmation,
        org_name: data.organization_name,
      });
      const { token, user } = response.data.data;
      login(token, user);
      toast.success('Account created! Welcome to FounderMind AI.');
      navigate('/dashboard');
    } catch (err) {
      const responseData = err?.response?.data;
      // Handle validation errors object (e.g. { errors: { email: ['already taken'] } })
      if (responseData?.errors) {
        const firstField = Object.values(responseData.errors)[0];
        const firstMessage = Array.isArray(firstField) ? firstField[0] : firstField;
        setApiError(firstMessage);
      } else {
        setApiError(
          responseData?.message ||
          responseData?.error ||
          'Something went wrong. Please try again.'
        );
      }
    }
  };

  const inputClass = (hasError) =>
    clsx(
      'w-full border rounded-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 transition',
      hasError
        ? 'border-red-400 bg-red-50 focus:ring-red-400'
        : 'border-neutral-300 bg-white'
    );

  const fieldError = (field) =>
    errors[field] ? (
      <p className="mt-1 text-xs text-red-600">{errors[field].message}</p>
    ) : null;

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading font-bold text-primary-600">FounderMind AI</h1>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-modal p-8">
          {/* Heading */}
          <div className="mb-6">
            <h2 className="text-2xl font-heading font-bold text-neutral-900">Create your account</h2>
            <p className="mt-1 text-sm text-neutral-500">Start assessing founders with AI</p>
          </div>

          {/* API Error Alert */}
          {apiError && (
            <div className="mb-5 flex items-start gap-3 rounded-lg bg-red-50 border border-red-200 px-4 py-3">
              <ExclamationCircleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-sm text-red-700">{apiError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
            {/* Full Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-neutral-700 mb-1">
                Full name
              </label>
              <input
                id="name"
                type="text"
                autoComplete="name"
                placeholder="Jane Smith"
                {...register('name')}
                className={inputClass(!!errors.name)}
              />
              {fieldError('name')}
            </div>

            {/* Organization Name */}
            <div>
              <label htmlFor="organization_name" className="block text-sm font-medium text-neutral-700 mb-1">
                Organization name
              </label>
              <input
                id="organization_name"
                type="text"
                autoComplete="organization"
                placeholder="Acme Ventures"
                {...register('organization_name')}
                className={inputClass(!!errors.organization_name)}
              />
              {fieldError('organization_name')}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                {...register('email')}
                className={inputClass(!!errors.email)}
              />
              {fieldError('email')}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-neutral-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Min. 8 characters"
                  {...register('password')}
                  className={clsx(inputClass(!!errors.password), 'pr-10')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400 hover:text-neutral-600 transition"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <EyeIcon className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              </div>
              {fieldError('password')}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="password_confirmation" className="block text-sm font-medium text-neutral-700 mb-1">
                Confirm password
              </label>
              <div className="relative">
                <input
                  id="password_confirmation"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Re-enter your password"
                  {...register('password_confirmation')}
                  className={clsx(inputClass(!!errors.password_confirmation), 'pr-10')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-neutral-400 hover:text-neutral-600 transition"
                  aria-label={showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <EyeIcon className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              </div>
              {fieldError('password_confirmation')}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className={clsx(
                'w-full flex items-center justify-center gap-2 rounded-input px-4 py-2.5 text-sm font-semibold text-white transition',
                isSubmitting
                  ? 'bg-primary-400 cursor-not-allowed'
                  : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
              )}
            >
              {isSubmitting && (
                <svg
                  className="h-4 w-4 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
              )}
              {isSubmitting ? 'Creating account…' : 'Create account'}
            </button>
          </form>

          {/* Footer link */}
          <p className="mt-6 text-center text-sm text-neutral-500">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary-600 hover:text-primary-700">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
